// react
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack } from '@mui/material';

// api
import {
  daluxfmServerOpenApi,
  useCreateTicketMutation,
  useSendMessagePhotoMutation,
  useUploadFileMutation,
} from '@/api/generated/daluxfm/daluxfm';
import type { LocationDto } from '@/api/generated/recalc/endpoints/v2/locations';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import DaluxTicketFields from './DaluxTicketFields';
import {
  toDaluxTicketFormValues,
  toDaluxTicketPayload,
} from './daluxTicketForm.mappers';
import {
  createDaluxTicketFormSchema,
  type DaluxBuildingOption,
  type DaluxTicketFormValues,
  type DaluxTopicOption,
} from './daluxTicketForm.schema';

// libraries
import { zodResolver } from '@hookform/resolvers/zod';
import type { ThunkDispatch } from '@reduxjs/toolkit';
import type { AnyAction } from 'redux';

type CreateDaluxTicketFormProps = {
  email?: string | null;
  topics: DaluxTopicOption[];
  buildingOptions: DaluxBuildingOption[];
  initialBuildingId?: string | null;
  energyGroupName?: string | null;
  remoniLocation?: LocationDto | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

export default function CreateDaluxTicketForm({
  email,
  topics,
  buildingOptions,
  initialBuildingId,
  energyGroupName,
  remoniLocation,
  onCancel,
  onSuccess,
}: CreateDaluxTicketFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation('dalux-integration');
  const dispatch = useDispatch() as ThunkDispatch<unknown, unknown, AnyAction>;

  const schema = createDaluxTicketFormSchema(t);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<DaluxTicketFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toDaluxTicketFormValues({
      reporterEmail: email,
      buildingId: initialBuildingId,
    }),
    mode: 'onTouched',
  });

  const [createTicket, { isLoading: isCreating }] = useCreateTicketMutation();
  const [uploadFile, { isLoading: isUploadingPhoto }] = useUploadFileMutation();
  const [sendMessagePhoto, { isLoading: isAttachingPhoto }] =
    useSendMessagePhotoMutation();

  const onSubmit = handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      const ticketResponse = await createTicket({
        ticket: toDaluxTicketPayload(values),
      }).unwrap();

      const ticketId = ticketResponse?.data?.ticketId;

      if (values.photo && ticketId) {
        const uploadSessionRequest = dispatch(
          daluxfmServerOpenApi.endpoints.createUploadSession.initiate(
            undefined,
            { forceRefetch: true },
          ),
        );

        try {
          const uploadSession = await uploadSessionRequest.unwrap();
          const uploadSessionId = uploadSession?.data?.sessionId;

          if (!uploadSessionId) {
            throw new Error('Missing upload session id');
          }

          await uploadFile({
            uploadSessionId,
            'X-File-Name': values.photo.name,
            body: values.photo,
          }).unwrap();

          await sendMessagePhoto({
            ticketId,
            messagePhoto: {
              data: {
                uploadSessionIds: [uploadSessionId],
                message: 'test',
              },
            },
          }).unwrap();
        } catch {
          dispatch(
            showSnackbar({
              message: t('ticket.photo_upload_error'),
              severity: 'error',
            }),
          );
          return;
        } finally {
          uploadSessionRequest.unsubscribe();
        }
      }

      dispatch(
        showSnackbar({ message: t('ticket.created'), severity: 'success' }),
      );
      onSuccess?.();
    } catch {
      dispatch(
        showSnackbar({ message: t('ticket.create_error'), severity: 'error' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="dalux-ticket-create-form">
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Card variant="outlined">
          <CardContent>
            <DaluxTicketFields
              control={control}
              t={t}
              disabled={isCreating || isUploadingPhoto || isAttachingPhoto}
              topics={topics}
              buildingOptions={buildingOptions}
              energyGroupName={energyGroupName}
              remoniLocation={remoniLocation ?? undefined}
              showNoMatchingBuildingsWarning={
                !!remoniLocation && !initialBuildingId
              }
              showPhotoUpload
            />
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton onClick={onCancel} disabled={isSubmitting}>
            {t('ticket.cancel')}
          </CancelButton>
          <SubmitButton
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
          >
            {t('ticket.create')}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
